import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import multiguard from 'vue-router-multiguard';
import { usePostHog } from '@/composables/posthog'
import {
  ATTENDANCE_ROUTE,
  ATTENDANCE_SETTING_ROUTE,
  DASHBOARD_ROUTE,
  EMPLOYEE_ALLOWANCE_ROUTE,
  EMPLOYEE_ATTENDANCE_ROUTE,
  EMPLOYEE_CONTRACTS_ROUTE,
  EMPLOYEE_DOCUMENT_ROUTE,
  EMPLOYEE_GENERAL_ROUTE,
  EMPLOYEE_LEAVE_ROUTE,
  EMPLOYEE_OVERTIME_ROUTE,
  EMPLOYEE_PAYROLL_COMPONENT_ROUTE,
  EMPLOYEE_PAYROLL_DETAILS_ROUTE,
  EMPLOYEE_PAYROLL_ROUTE,
  EMPLOYEE_PAYROLL_SALARY_ROUTE,
  EMPLOYEE_PROMOTION_HISTORY_ROUTE,
  EMPLOYEE_SETTINGS_ROUTE,
  EMPLOYEE_ACTIVITY_ROUTE,
  EMPLOYEES_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  HOLIDAYS_ROUTE,
  LEAVE_TYPES_ROUTE,
  LEAVES_ROUTE,
  LOGIN_ROUTE,
  ORGANIZATION_STRUCTURE_ROUTE,
  OVERTIME_ROUTE,
  PAYROLL_DETAILS_ROUTE,
  PAYROLL_ROUTE,
  PAYROLL_SETTINGS_ROUTE,
  PAYROLL_SUMMARY_ROUTE,
  PROFILE_ALLOWANCE_ROUTE,
  PROFILE_ATTENDANCE_ROUTE,
  PROFILE_GENERAL_ROUTE,
  PROFILE_LEAVE_ROUTE,
  PROFILE_OVERTIME_ROUTE,
  PROFILE_PAYROLL_ROUTE,
  PROVACY_POLICY_ROUTE,
  JOB_POST_DETAILS_ROUTE,
  ADD_JOB_POST_ROUTE,
  EDIT_JOB_POST_ROUTE,
  RECRUITMENT_ROUTE,
  REGISTER_EMPLOYEE_ROUTE,
  RESET_PASSWORD_ROUTE,
  GENERAL_SETTINGS_ROUTE,
  SECURITY_SETTINGS_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  SIGNUP_ROUTE,
  JOB_APPLICATIONS_ROUTE,
  PUBLIC_JOB_APPLICATION_ROUTE,
  PROJECTS_ROUTE,
  PROJECT_DETAILS_ROUTE,
  REPORTS_ROUTE,
  REPORT_BUILDER_ROUTE,
  EXPORT_TEMPLATES_ROUTE,
  CREATE_EXPORT_TEMPLATE_ROUTE,
  EDIT_EXPORT_TEMPLATE_ROUTE,
  OFFBOARDING_SETTINGS_ROUTE,
  EMPLOYEE_OFFBOARDING_ROUTE,
  KNOWLEDGE_BASE_ROUTE,
  KNOWLEDGE_BASE_ARTICLE_ROUTE,
  HELP_CENTER_ROUTE,
  HELP_SECTION_ROUTE,
  HELP_ARTICLE_ROUTE,
  CHAT_ROUTE,
  CHAT_CONVERSATION_ROUTE,
  FILES_ROUTE,
  FILES_FOLDER_ROUTE,
} from '@/router/routes';
import { ifNotAuthDeny } from '@/router/guards/not_auth';
import { ifAuthDeny } from '@/router/guards/auth';
import { ifModuleDisabledDeny, syncOrganizationModules } from '@/router/guards/module';
import DashboardView from '@/views/Dashboard.vue';
import LoginView from '@/views/auth/Login.vue';
import { ifNotAdminDeny } from './guards/role';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: DASHBOARD_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: DashboardView,
  },
  {
    path: '/login',
    name: LOGIN_ROUTE,
    beforeEnter: ifAuthDeny,
    component: LoginView,
  },
  {
    path: '/signup',
    name: SIGNUP_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import('@/views/auth/Signup.vue'),
  },
  {
    path: '/forgot-password',
    name: FORGOT_PASSWORD_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import('@/views/auth/Forgot_Password.vue'),
  },
  {
    path: '/reset-password/:token',
    name: RESET_PASSWORD_ROUTE,
    beforeEnter: ifAuthDeny,
    component: () => import('@/views/auth/Reset_Password.vue'),
  },
  {
    path: '/profile/:id/general',
    name: PROFILE_GENERAL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/profile/General.vue'),
  },
  {
    path: '/profile/:id/payroll',
    name: PROFILE_PAYROLL_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/profile/Payroll.vue'),
  },
  {
    path: '/profile/:id/allowance',
    name: PROFILE_ALLOWANCE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/profile/Payroll.vue'),
  },
  {
    path: '/profile/:id/leave',
    name: PROFILE_LEAVE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/profile/Leave.vue'),
  },
  {
    path: '/profile/:id/overtime',
    name: PROFILE_OVERTIME_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/profile/Overtime.vue'),
  },
  {
    path: '/profile/:id/attendance',
    name: PROFILE_ATTENDANCE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/profile/Attendance.vue'),
  },
  {
    path: '/employees',
    name: EMPLOYEES_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Employees.vue'),
  },
  {
    path: '/employees/register',
    name: REGISTER_EMPLOYEE_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Register_Employee.vue'),
  },
  {
    path: '/employee/:id/general',
    name: EMPLOYEE_GENERAL_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/General.vue'),
  },
  {
    path: '/employee/:id/payroll',
    name: EMPLOYEE_PAYROLL_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Payroll.vue'),
  },
  {
    path: '/employee/:id/payroll-salary',
    name: EMPLOYEE_PAYROLL_SALARY_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Payroll.vue'),
  },
  {
    path: '/employee/:id/payroll-component',
    name: EMPLOYEE_PAYROLL_COMPONENT_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Payroll.vue'),
  },
  {
    path: '/employee/:id/payroll-promotion-history',
    name: EMPLOYEE_PROMOTION_HISTORY_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Payroll.vue'),
  },
  {
    path: '/employee/:id/allowance',
    name: EMPLOYEE_ALLOWANCE_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Payroll.vue'),
  },
  {
    path: '/employee/:id/leave',
    name: EMPLOYEE_LEAVE_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Leave.vue'),
  },
  {
    path: '/employee/:id/overtime',
    name: EMPLOYEE_OVERTIME_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Overtime.vue'),
  },
  {
    path: '/employee/:id/attendance',
    name: EMPLOYEE_ATTENDANCE_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Attendance.vue'),
  },
  {
    path: '/employee/:id/contracts',
    name: EMPLOYEE_CONTRACTS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Contracts.vue'),
  },
  {
    path: '/employee/:id/document',
    name: EMPLOYEE_DOCUMENT_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Document.vue'),
  },
  {
    path: '/employee/:id/settings',
    name: EMPLOYEE_SETTINGS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Settings.vue'),
  },
  {
    path: '/employee/:id/activity',
    name: EMPLOYEE_ACTIVITY_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Activity.vue'),
  },
  {
    path: '/employee/:id/offboarding',
    name: EMPLOYEE_OFFBOARDING_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/employee/Offboarding.vue'),
  },
  {
    path: '/payroll',
    name: PAYROLL_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/payroll/Payroll.vue'),
  },
  {
    path: '/payroll/:id/summary',
    name: PAYROLL_SUMMARY_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/payroll/Payroll_Summary.vue'),
  },
  {
    path: '/payroll/:id/details',
    name: PAYROLL_DETAILS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/payroll/Payroll_Details.vue'),
  },
  {
    path: '/payroll-slip/:employeeId/:month/:year',
    name: EMPLOYEE_PAYROLL_DETAILS_ROUTE,
    // beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/payroll/Payroll_Slip.vue'),
  },
  {
    path: '/payroll-settings',
    name: PAYROLL_SETTINGS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/payroll/Payroll_Settings.vue'),
  },
  {
    path: '/leave',
    name: LEAVES_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/leave/Leave.vue'),
  },
  {
    path: '/holidays',
    name: HOLIDAYS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/leave/Holidays.vue'),
  },
  {
    path: '/leave-type',
    name: LEAVE_TYPES_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/leave/Leave_Type.vue'),
  },
  {
    path: '/attendance',
    name: ATTENDANCE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Attendance/Attendance.vue'),
  },
  {
    path: '/attendance/setting',
    name: ATTENDANCE_SETTING_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Attendance/Attendance_Setting.vue'),
  },
  {
    path: '/overtime',
    name: OVERTIME_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/overtime/Overtime.vue'),
  },
  {
    path: '/recruitment',
    name: RECRUITMENT_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Recruitment/Recruitment.vue'),
  },
  {
    path: '/recruitment/job-post/add',
    name: ADD_JOB_POST_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Recruitment/Add_Job_Post.vue'),
  },
  {
    path: '/recruitment/job-post/:id/edit',
    name: EDIT_JOB_POST_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Recruitment/Edit_Job_Post.vue'),
  },
  {
    path: '/recruitment/job-post/:id',
    name: JOB_POST_DETAILS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Recruitment/Job_Post_Detail.vue'),
  },
  {
    path: '/recruitment/job-applications/:id',
    name: JOB_APPLICATIONS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Recruitment/Job_Applications.vue'),
  },
  {
    path: '/organization-structure',
    name: ORGANIZATION_STRUCTURE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Organization_Structure.vue'),
  },
  {
    path: '/settings/general',
    name: GENERAL_SETTINGS_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Settings/General_Settings.vue'),
  },
  {
    path: '/settings/security',
    name: SECURITY_SETTINGS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Settings/Security_Settings.vue'),
  },
  {
    path: '/settings/calendar',
    name: CALENDAR_SETTINGS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Settings/Calendar_Settings.vue'),
  },
  {
    path: '/settings/export-templates',
    name: EXPORT_TEMPLATES_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Settings/Export_Templates.vue'),
  },
  {
    // query: dataset (required), starter (optional ready-made template key)
    path: '/settings/export-templates/new',
    name: CREATE_EXPORT_TEMPLATE_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Settings/Export_Template_Builder.vue'),
  },
  {
    path: '/settings/export-templates/:id',
    name: EDIT_EXPORT_TEMPLATE_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Settings/Export_Template_Builder.vue'),
  },
  // Export templates used to be payroll-only; keep old bookmarks working.
  {
    path: '/settings/payroll',
    redirect: { name: EXPORT_TEMPLATES_ROUTE },
  },
  {
    path: '/settings/payroll/template',
    redirect: { name: CREATE_EXPORT_TEMPLATE_ROUTE, query: { dataset: 'payslips' } },
  },
  {
    path: '/settings/payroll/template/:id',
    redirect: (to) => ({ name: EDIT_EXPORT_TEMPLATE_ROUTE, params: { id: to.params.id } }),
  },
  {
    path: '/settings/offboarding',
    name: OFFBOARDING_SETTINGS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Settings/Employee_Offboarding_Template.vue'),
  },
  {
    path: '/privacy-policy',
    name: PROVACY_POLICY_ROUTE,
    component: () => import('@/views/Privacy_Policy.vue'),
  },
  {
    path: '/recruitment/job-post-application/:id',
    name: PUBLIC_JOB_APPLICATION_ROUTE,
    component: () => import('@/views/Recruitment/Public_Job_Application.vue'),
  },
  {
    path: '/projects',
    name: PROJECTS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Projects/Projects.vue'),
  },
  {
    path: '/projects/:id',
    name: PROJECT_DETAILS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Projects/Project_Details.vue'),
  },
  {
    path: '/reports',
    name: REPORTS_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Reports/Reports.vue'),
  },
  {
    path: '/reports/:type',
    name: REPORT_BUILDER_ROUTE,
    beforeEnter: multiguard([ifNotAuthDeny, ifNotAdminDeny]),
    component: () => import('@/views/Reports/Report_Builder.vue'),
  },
  {
    path: '/knowledge-base',
    name: KNOWLEDGE_BASE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Knowledge_Base/Knowledge_Base.vue'),
  },
  {
    path: '/knowledge-base/:id',
    name: KNOWLEDGE_BASE_ARTICLE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Knowledge_Base/Knowledge_Base_Article.vue'),
  },
  {
    path: '/help',
    name: HELP_CENTER_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Help/Help_Center.vue'),
  },
  {
    path: '/help/:section',
    name: HELP_SECTION_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Help/Help_Section.vue'),
  },
  {
    path: '/help/:section/:article',
    name: HELP_ARTICLE_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Help/Help_Article.vue'),
  },
  {
    path: '/chat',
    name: CHAT_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Chat/Chat.vue'),
  },
  {
    path: '/chat/:id',
    name: CHAT_CONVERSATION_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Chat/Chat.vue'),
  },
  {
    path: '/files',
    name: FILES_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Files/Files.vue'),
  },
  {
    path: '/files/folder/:id',
    name: FILES_FOLDER_ROUTE,
    beforeEnter: ifNotAuthDeny,
    component: () => import('@/views/Files/Files.vue'),
  },
];

const { posthog } = usePostHog()

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Pages that belong to a module the organization is not licensed for are not
// reachable, whichever way the URL was typed or linked.
router.beforeEach(ifModuleDisabledDeny);

// Keep the licensed module list fresh while the user moves around.
router.afterEach(syncOrganizationModules);

export default router;
